 // Service for Google Maps API integration
import {
  ParsedAddress,
  ParsedAddressComponents,
  GoogleGeocodingResult,
  GoogleAddressComponent,
  AddressSuggestion
} from './types';

declare global {
  interface Window {
    google: any;
  }
}

class GoogleMapsService {
  private static instance: GoogleMapsService;
  private isLoaded = false;
  private loadPromise: Promise<void> | null = null;
  private placesService: any = null;
  private autocompleteService: any = null;

  private constructor() {}

  static getInstance(): GoogleMapsService {
    if (!GoogleMapsService.instance) {
      GoogleMapsService.instance = new GoogleMapsService();
    }
    return GoogleMapsService.instance;
  }

  /**
   * Load the Google Maps JavaScript API (bootstrap) and the Places library.
   *
   * We intentionally do NOT use the legacy `google.maps.places.Autocomplete`
   * widget (deprecated for new customers since March 2025). Instead we load the
   * Places library and rely on the modern `AutocompleteSuggestion` API (with a
   * graceful fallback to the lightweight `AutocompleteService`).
   */
  async loadAPI(apiKey: string): Promise<void> {
    if (this.isLoaded) {
      return;
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = (async () => {
      const existing = window.google && window.google.maps;

      if (!existing) {
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          // Explicitly request the `places` package. This is what the historical
          // widget relied on, so it guarantees `AutocompleteService`/`PlacesService`
          // are available (the library is still loaded on demand via
          // `importLibrary('places')` in `ensurePlacesLibrary` as a safety net).
          script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
            apiKey
          )}&v=weekly&language=fr&libraries=places&loading=async`;
          script.async = true;
          script.defer = true;

          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load Google Maps API'));

          document.head.appendChild(script);
        });
      }

      await this.ensurePlacesLibrary();
      this.isLoaded = true;
    })();

    try {
      await this.loadPromise;
    } catch (error) {
      this.loadPromise = null;
      throw error;
    }
  }

  /**
   * Make sure the `places` package is available, using the modern
   * `google.maps.importLibrary` loader when present.
   */
  private async ensurePlacesLibrary(): Promise<void> {
    const maps = (window.google as any)?.maps;
    if (!maps) {
      return;
    }

    const places = maps.places;
    const alreadyAvailable = !!(
      places &&
      (places.AutocompleteSuggestion || places.AutocompleteService || places.PlacesService)
    );

    if (alreadyAvailable) {
      return;
    }

    if (typeof maps.importLibrary === 'function') {
      await maps.importLibrary('places');
    }
  }

  /**
   * Check if API is loaded
   */
  isAPILoaded(): boolean {
    return this.isLoaded && !!window.google && !!window.google.maps;
  }

  /**
   * Check if any Places data source is available (modern or fallback).
   */
  isPlacesAvailable(): boolean {
    const places = (window.google as any)?.maps?.places;
    return !!(
      places &&
      (places.AutocompleteSuggestion || places.AutocompleteService || places.PlacesService)
    );
  }

  /**
   * Parse address components from Google result (Geocoding or Places)
   */
  parseAddressComponents(result: GoogleGeocodingResult): ParsedAddress {
    const components: ParsedAddressComponents = {};
    const rawComponents = result.address_components || [];

    // Extract address components
    rawComponents.forEach((component: GoogleAddressComponent) => {
      const types = component.types;
      if (types.includes('street_number')) {
        components.street_number = component.long_name;
      } else if (types.includes('route')) {
        components.route = component.long_name;
      } else if (types.includes('locality')) {
        components.locality = component.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        components.administrative_area_level_1 = component.long_name;
      } else if (types.includes('administrative_area_level_2')) {
        components.administrative_area_level_2 = component.long_name;
      } else if (types.includes('country')) {
        components.country = component.long_name;
      } else if (types.includes('postal_code')) {
        components.postal_code = component.long_name;
      }
    });

    // Build address lines
    const streetNumber = components.street_number || '';
    const route = components.route || '';
    const addressLine1 = `${streetNumber} ${route}`.trim();

    const addressLine2 = components.administrative_area_level_2 || '';
    const city = components.locality || '';
    const state = components.administrative_area_level_1 || '';
    const postalCode = components.postal_code || '';
    const country = components.country || '';

    const location = result.geometry?.location;
    const latitude = location ? location.lat() : 0;
    const longitude = location ? location.lng() : 0;

    const formatted = result.formatted_address || '';

    return {
      address_line1: addressLine1 || formatted.split(',')[0]?.trim() || '',
      address_line2: addressLine2,
      city: city || formatted.split(',')[1]?.trim() || '',
      state,
      postal_code: postalCode,
      country,
      latitude,
      longitude,
      formatted_address: formatted,
      place_id: result.place_id || ''
    };
  }

  /**
   * Fetch address suggestions for a free-text query.
   *
   * Tries, in order: `AutocompleteService` (legacy Places API, the same one the
   * historical widget relied on), then the modern `AutocompleteSuggestion`,
   * then forward geocoding as a last resort.
   */
  async autocompleteAddresses(query: string): Promise<AddressSuggestion[]> {
    const input = query.trim();
    if (!input || !this.isAPILoaded()) {
      return [];
    }

    const places = (window.google as any)?.maps?.places;

    if (!places) {
      console.warn(
        'Google Maps "places" library is unavailable; only the geocoding fallback is used (no type-ahead suggestions).'
      );
    }

    // Preferred: lightweight `AutocompleteService` (legacy Places API, the same
    // one the historical widget relied on, so it works with the existing key).
    if (places?.AutocompleteService) {
      try {
        const service = this.getAutocompleteService();
        const predictions: any[] = await new Promise((resolve) => {
          service.getPlacePredictions(
            { input, language: 'fr' },
            (results: any[], status: string) => {
              if (status !== 'OK' || !results) {
                resolve([]);
                return;
              }
              resolve(results);
            }
          );
        });

        if (predictions.length > 0) {
          return predictions.map((prediction: any) => ({
            placeId: prediction.place_id,
            primaryText: prediction.structured_formatting?.main_text || prediction.description || '',
            secondaryText: prediction.structured_formatting?.secondary_text || '',
            fullText: prediction.description || ''
          }));
        }
      } catch (error) {
        console.warn('AutocompleteService failed, trying modern AutocompleteSuggestion', error);
      }
    }

    // Secondary: modern `AutocompleteSuggestion` (requires Places API (New)).
    if (places?.AutocompleteSuggestion) {
      try {
        const request: any = {
          input,
          language: 'fr'
        };
        const response = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        const suggestions: any[] = response?.suggestions || [];

        const mapped = suggestions
          .map((suggestion: any) => suggestion?.placePrediction)
          .filter((prediction: any) => !!prediction)
          .map((prediction: any) => ({
            placeId: prediction.placeId,
            primaryText: prediction.mainText?.text || prediction.text?.text || '',
            secondaryText: prediction.secondaryText?.text || '',
            fullText: prediction.text?.text || ''
          }));

        if (mapped.length > 0) {
          return mapped;
        }
      } catch (error) {
        console.warn('AutocompleteSuggestion failed, falling back to geocoding', error);
      }
    }

    // Last resort: forward geocoding, always available with the Geocoding API.
    // The address is pre-resolved so the picker needs no further Places lookup.
    try {
      const parsed = await this.geocodeAddress(input);
      if (parsed) {
        const label = parsed.formatted_address || input;
        return [
          {
            placeId: parsed.place_id,
            primaryText: label,
            secondaryText: '',
            fullText: label,
            parsedAddress: parsed
          }
        ];
      }
    } catch {
      // No match for this query.
    }

    return [];
  }

  /**
   * Resolve a place id into a full structured address via `PlacesService`.
   */
  async getPlaceAddress(placeId: string): Promise<ParsedAddress | null> {
    const places = (window.google as any)?.maps?.places;
    if (!places || !placeId) {
      return null;
    }

    const service = this.getPlacesService();
    const place: any = await new Promise((resolve, reject) => {
      service.getDetails(
        {
          placeId,
          fields: ['address_components', 'geometry', 'formatted_address', 'place_id']
        },
        (result: any, status: string) => {
          if (status === 'OK' && result) {
            resolve(result);
          } else {
            reject(new Error(`Place details failed: ${status}`));
          }
        }
      );
    });

    return this.parseAddressComponents(place as GoogleGeocodingResult);
  }

  /**
   * Resolve a free-text query into a structured address.
   *
   * Prefers the autocomplete → place details flow (exact match, correct
   * formatting) and only falls back to forward geocoding when needed.
   */
  async resolveQuery(query: string): Promise<ParsedAddress | null> {
    const input = query.trim();
    if (!input) {
      return null;
    }

    const suggestions = await this.autocompleteAddresses(input);
    if (suggestions.length > 0 && suggestions[0].placeId) {
      try {
        const address = await this.getPlaceAddress(suggestions[0].placeId);
        if (address) {
          return address;
        }
      } catch (error) {
        console.warn('Place details resolution failed, falling back to geocoding', error);
      }
    }

    try {
      return await this.geocodeAddress(input);
    } catch {
      return null;
    }
  }

  /**
   * Geocode an address string to get coordinates (fallback only).
   */
  async geocodeAddress(address: string): Promise<ParsedAddress | null> {
    if (!this.isAPILoaded()) {
      throw new Error('Google Maps API not loaded');
    }

    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ address }, (results: any, status: any) => {
        if (status === 'OK' && results && results.length > 0) {
          const parsedAddress = this.parseAddressComponents(results[0] as any);
          resolve(parsedAddress);
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  }

  /**
   * Reverse geocode coordinates to get address
   */
  async reverseGeocode(lat: number, lng: number): Promise<ParsedAddress | null> {
    if (!this.isAPILoaded()) {
      throw new Error('Google Maps API not loaded');
    }

    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();
      const latLng = new window.google.maps.LatLng(lat, lng);

      geocoder.geocode({ location: latLng }, (results: any, status: any) => {
        if (status === 'OK' && results && results.length > 0) {
          const parsedAddress = this.parseAddressComponents(results[0] as any);
          resolve(parsedAddress);
        } else {
          reject(new Error(`Reverse geocoding failed: ${status}`));
        }
      });
    });
  }

  private getAutocompleteService(): any {
    if (!this.autocompleteService) {
      const places = (window.google as any).maps.places;
      this.autocompleteService = new places.AutocompleteService();
    }
    return this.autocompleteService;
  }

  private getPlacesService(): any {
    if (!this.placesService) {
      const places = (window.google as any).maps.places;
      // PlacesService requires a DOM node / map attachment (an off-screen div is enough).
      const holder = document.createElement('div');
      this.placesService = new places.PlacesService(holder);
    }
    return this.placesService;
  }

  /**
   * Get static map URL
   */
  getStaticMapUrl(
    lat: number,
    lng: number,
    options: {
      width?: number;
      height?: number;
      zoom?: number;
      markerColor?: string;
      apiKey: string;
    }
  ): string {
    const {
      width = 400,
      height = 300,
      zoom = 15,
      markerColor = 'red',
      apiKey
    } = options;

    const marker = `color:${markerColor}|${lat},${lng}`;

    return `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=${zoom}&size=${width}x${height}&markers=${marker}&key=${apiKey}`;
  }

  /**
   * Get Google Maps directions URL
   */
  getDirectionsUrl(lat: number, lng: number): string {
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  }

  /**
   * Validate if coordinates are valid
   */
  isValidCoordinates(latitude: number, longitude: number): boolean {
    return (
      latitude >= -90 && latitude <= 90 &&
      longitude >= -180 && longitude <= 180
    );
  }

  /**
   * Calculate distance between two points in kilometers
   */
  calculateDistance(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ): number {
    if (!this.isAPILoaded()) {
      // Fallback calculation using Haversine formula
      const R = 6371; // Earth's radius in km
      const dLat = this.toRad(lat2 - lat1);
      const dLng = this.toRad(lng2 - lng1);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    }

    // Use Google Maps geometry library if available
    const point1 = new window.google.maps.LatLng(lat1, lng1);
    const point2 = new window.google.maps.LatLng(lat2, lng2);
    return window.google.maps.geometry.spherical.computeDistanceBetween(point1, point2) / 1000;
  }

  private toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }
}

export default GoogleMapsService.getInstance();
